import { CalloutBlock } from "@bezoned/shared/components/callout-block.jsx";
import { routes } from "@bezoned/shared/routes.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useCallback, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { FullScreenLoader } from "../components/ui/loaders/full-screen-loader.js";
import { useAdminConsent } from "../features/auth/use-admin-consent.js";
import { useAuth } from "../features/auth/use-auth.js";
import { PermissionUpgradeNotAvailable } from "../features/onboarding/permission-upgrade-not-available.js";
import { useTenant } from "../features/onboarding/tenant.context.js";
import { tenantPermissionVersionUpdate } from "../services/api.service.js";
import { recordErrorInSentry } from "../utils/init-sentry.js";
import { updateSearchParams } from "../utils/url-search-params.js";

export const PermissionUpgradePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const tenant = useTenant();

  const showPermissionUpgradeBanner =
    tenant.type !== "enterprise" && tenant.permissions.needsUpgrade;

  const [hasGrantedConsent, setHasGrantedConsent] = useQueryState(
    "granted",
    parseAsBoolean
  );
  const [hasConsentError, setHasConsentError] = useQueryState(
    "error",
    parseAsBoolean
  );
  const openAdminConsent = useAdminConsent({
    flow: "permission-upgrade",
    tenantId: user.tenantId,
  });

  const updateVersionMutation = useMutation({
    mutationFn: tenantPermissionVersionUpdate,
    onSuccess: async () => {
      setHasGrantedConsent(null);
      setHasConsentError(null);
      navigate(
        {
          pathname: routes.protected.chooseTeam,
          search: updateSearchParams(location.search, {
            remove: ["granted", "error"],
          }),
        },
        { replace: true }
      );
      await queryClient.invalidateQueries({
        queryKey: ["tenantExists"],
      });
    },
  });

  const finalizePermissionUpgrade = useCallback(() => {
    updateVersionMutation.mutate(undefined, {
      onError: (error: unknown) => {
        recordErrorInSentry(
          error instanceof Error ? error : new Error(String(error))
        );
        setHasGrantedConsent(null);
        setHasConsentError(true);
      },
    });
  }, [setHasConsentError, setHasGrantedConsent, updateVersionMutation]);

  // Single trigger for both the Teams popup flow (result === "completed" sets
  // hasGrantedConsent) and the redirect-back flow (page mounts with ?granted=true).
  useEffect(() => {
    if (
      tenant.type === "enterprise" ||
      !hasGrantedConsent ||
      updateVersionMutation.isPending
    ) {
      return;
    }

    finalizePermissionUpgrade();
  }, [
    finalizePermissionUpgrade,
    hasGrantedConsent,
    tenant.type,
    updateVersionMutation.isPending,
  ]);

  const handleGiveConsent = useCallback(async () => {
    setHasConsentError(null);
    setHasGrantedConsent(null);

    try {
      const result = await openAdminConsent();
      if (result === "completed") {
        setHasGrantedConsent(true);
      }
      // "redirected" -> page navigates away, nothing to do here
    } catch (error: unknown) {
      recordErrorInSentry(error);
      setHasConsentError(true);
    }
  }, [openAdminConsent, setHasConsentError, setHasGrantedConsent]);

  const handleReturnToLobby = useCallback(() => {
    navigate(
      {
        pathname: routes.protected.chooseTeam,
        search: updateSearchParams(location.search, {
          remove: ["granted", "error"],
        }),
      },
      { replace: true }
    );
  }, [location.search, navigate]);

  if (tenant.type === "enterprise") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <PermissionUpgradeNotAvailable onReturnToLobby={handleReturnToLobby} />
      </div>
    );
  }

  if (!showPermissionUpgradeBanner) {
    return (
      <Navigate
        replace
        to={{
          pathname: routes.protected.chooseTeam,
          search: updateSearchParams(location.search, {
            remove: ["granted", "error"],
          }),
        }}
      />
    );
  }

  if (updateVersionMutation.isPending) {
    return <FullScreenLoader message="Saving updated permissions..." />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 p-8">
      <div className="w-full max-w-xl rounded-lg bg-white p-8 shadow-md">
        <h1 className="mb-4 text-2xl font-bold text-gray-900">
          Updated Permissions Required
        </h1>

        <p className="mb-4 text-gray-700">
          BeZoned has been updated and requires you to accept Microsoft
          permissions again. Please continue to approve the update. Admin rights
          are required.
        </p>

        <p className="mb-6 text-gray-700">
          Only a user with admin rights can grant this consent. When you
          continue, Microsoft will open the consent dialog for approval.
        </p>

        {Boolean(hasConsentError) && (
          <CalloutBlock variant="error">
            <p>
              The updated permissions were not granted. Please try again or ask
              a team administrator to approve them for you.
            </p>
          </CalloutBlock>
        )}

        <button
          className="bg-bzd-button hover:bg-bzd-button-hover mt-6 block w-full rounded-md px-4 py-2 text-center text-white transition-colors"
          onClick={handleGiveConsent}
          type="button"
        >
          Give Consent
        </button>
      </div>
    </div>
  );
};
